
import React, { useEffect, useState } from 'react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogHeader,
    AlertDialogTitle,
} from './alert-dialog';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';
import { Check, XCircle, Trash2, Info } from 'lucide-react';

export type ConfirmDialogVariant = 'default' | 'destructive' | 'success' | 'warning' | 'info';

export interface ConfirmDialogNotice {
    /** Bold heading shown at the top of the notice box (e.g. "What happens:") */
    heading?: string;
    /** List of bullet-point strings */
    items: React.ReactNode[];
}

interface ConfirmDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description: React.ReactNode;
    /**
     * Optional highlighted notice box rendered below the description.
     * Styled to match the variant colour (amber for warning, red for destructive, etc.)
     */
    notice?: ConfirmDialogNotice;
    /**
     * When set, confirm stays disabled until the user checks this acknowledgement.
     * Use for irreversible actions that retire purchased inventory (e.g. hard-delete + plaque).
     */
    acknowledgementLabel?: React.ReactNode;
    confirmText?: React.ReactNode;
    cancelText?: React.ReactNode;
    variant?: ConfirmDialogVariant;
    onConfirm: () => void;
    isLoading?: boolean;
    icon?: React.ReactNode;
    hideCancel?: boolean;
}

export function ConfirmDialog({
    open,
    onOpenChange,
    title,
    description,
    notice,
    acknowledgementLabel,
    confirmText,
    cancelText,
    variant = 'default',
    onConfirm,
    isLoading = false,
    icon,
    hideCancel = false,
}: ConfirmDialogProps) {
    const { t } = useTranslation();
    const [acked, setAcked] = useState(false);
    const resolvedConfirmText = confirmText ?? t('common.confirm', 'Confirm');
    const resolvedCancelText = cancelText ?? t('common.cancel', 'Cancel');

    useEffect(() => {
        if (open) setAcked(false);
    }, [open, acknowledgementLabel]);

    const confirmDisabled = isLoading || (!!acknowledgementLabel && !acked);
    const getVariantStyles = () => {
        switch (variant) {
            case 'success':
                return {
                    iconBg: 'bg-green-100 dark:bg-green-900/30',
                    iconText: 'text-green-600 dark:text-green-400',
                    button: 'bg-green-600 hover:bg-green-700 text-white shadow-sm',
                    defaultIcon: <Check className="h-5 w-5" strokeWidth={2.5} />,
                    noticeBorder: 'border-green-200 dark:border-green-800',
                    noticeBg: 'bg-green-50 dark:bg-green-950/20',
                    noticeHeading: 'text-green-800 dark:text-green-400',
                    noticeText: 'text-green-700 dark:text-green-500',
                };
            case 'warning':
                return {
                    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
                    iconText: 'text-amber-600 dark:text-amber-400',
                    button: 'bg-amber-600 hover:bg-amber-700 text-white shadow-sm',
                    defaultIcon: <XCircle className="h-5 w-5" strokeWidth={2.5} />,
                    noticeBorder: 'border-amber-200 dark:border-amber-800',
                    noticeBg: 'bg-amber-50 dark:bg-amber-950/20',
                    noticeHeading: 'text-amber-800 dark:text-amber-400',
                    noticeText: 'text-amber-700 dark:text-amber-500',
                };
            case 'destructive':
                return {
                    iconBg: 'bg-red-100 dark:bg-red-900/30',
                    iconText: 'text-red-600 dark:text-red-400',
                    button: 'bg-red-600 hover:bg-red-700 text-white shadow-sm',
                    defaultIcon: <Trash2 className="h-5 w-5" strokeWidth={2.5} />,
                    noticeBorder: 'border-red-200 dark:border-red-800',
                    noticeBg: 'bg-red-50 dark:bg-red-950/20',
                    noticeHeading: 'text-red-700 dark:text-red-400',
                    noticeText: 'text-red-600 dark:text-red-500',
                };
            case 'info':
                return {
                    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
                    iconText: 'text-blue-600 dark:text-blue-400',
                    button: 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm',
                    defaultIcon: <Info className="h-5 w-5" strokeWidth={2.5} />,
                    noticeBorder: 'border-blue-200 dark:border-blue-800',
                    noticeBg: 'bg-blue-50 dark:bg-blue-950/20',
                    noticeHeading: 'text-blue-800 dark:text-blue-400',
                    noticeText: 'text-blue-700 dark:text-blue-500',
                };
            default:
                return {
                    iconBg: 'bg-primary/10',
                    iconText: 'text-primary',
                    button: 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm',
                    defaultIcon: <Info className="h-5 w-5" strokeWidth={2.5} />,
                    noticeBorder: 'border-border',
                    noticeBg: 'bg-muted/50',
                    noticeHeading: 'text-foreground',
                    noticeText: 'text-muted-foreground',
                };
        }
    };

    const styles = getVariantStyles();

    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent className="max-w-[480px] p-0 gap-0 border border-border/40 shadow-xl bg-background sm:rounded-xl overflow-hidden">
                <div className="flex flex-col sm:flex-row p-6 gap-5">
                    {/* Icon Column */}
                    <div className={cn(
                        "mx-auto sm:mx-0 flex flex-shrink-0 items-center justify-center rounded-full h-12 w-12 sm:h-10 sm:w-10",
                        styles.iconBg
                    )}>
                        <div className={cn("h-5 w-5", styles.iconText)}>
                            {icon || styles.defaultIcon}
                        </div>
                    </div>

                    {/* Content Column */}
                    <div className="flex-1 text-center sm:text-left space-y-3">
                        <AlertDialogHeader className="space-y-2">
                            <AlertDialogTitle className="text-lg font-semibold text-foreground">
                                {title}
                            </AlertDialogTitle>
                            <AlertDialogDescription className="text-sm text-muted-foreground leading-relaxed">
                                {description}
                            </AlertDialogDescription>
                        </AlertDialogHeader>

                        {/* Optional notice box */}
                        {notice && notice.items.length > 0 && (
                            <div className={cn(
                                'rounded-lg border p-3 space-y-1.5 text-left',
                                styles.noticeBorder,
                                styles.noticeBg
                            )}>
                                {notice.heading && (
                                    <p className={cn('text-sm font-semibold', styles.noticeHeading)}>
                                        {notice.heading}
                                    </p>
                                )}
                                <ul className="space-y-1">
                                    {notice.items.map((item, i) => (
                                        <li key={i} className={cn('flex items-start gap-2 text-sm', styles.noticeText)}>
                                            <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-current" />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {acknowledgementLabel && (
                            <label className="flex items-start gap-2.5 text-left cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    className="mt-0.5 h-4 w-4 rounded border-input accent-current"
                                    checked={acked}
                                    onChange={(e) => setAcked(e.target.checked)}
                                    disabled={isLoading}
                                />
                                <span className="text-sm text-foreground leading-snug">
                                    {acknowledgementLabel}
                                </span>
                            </label>
                        )}
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="bg-muted/30 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 border-t border-border/40">
                    {!hideCancel && (
                        <AlertDialogCancel
                            className="mt-0 w-full sm:w-auto min-w-[80px] bg-background hover:bg-muted/50 border-input/60 shadow-sm"
                            disabled={isLoading}
                        >
                            {resolvedCancelText}
                        </AlertDialogCancel>
                    )}
                    <AlertDialogAction
                        onClick={(e) => {
                            e.preventDefault();
                            if (confirmDisabled) return;
                            onConfirm();
                        }}
                        disabled={confirmDisabled}
                        className={cn(
                            "w-full sm:w-auto min-w-[80px] font-medium transition-all focus:ring-2 focus:ring-offset-1",
                            variant !== 'default' && styles.button
                        )}
                    >
                        {isLoading ? (
                            <div className="flex items-center gap-2">
                                <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                <span>Wait...</span>
                            </div>
                        ) : resolvedConfirmText}
                    </AlertDialogAction>
                </div>
            </AlertDialogContent>
        </AlertDialog>
    );
}
